import React from 'react';

// API configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://portalapply.online/api/v1';

// Generic fetch wrapper with error handling
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// Cache management
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

export const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

export const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
  });
};

// Generic hook for API calls
export const useApi = (endpoint, options = {}) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [error, setError] = React.useState(null);
  const isFetchingRef = React.useRef(false);

  const fetchData = React.useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    const cacheKey = `${endpoint}${JSON.stringify(options)}`;
    const cached = getCachedData(cacheKey);
    
    if (cached && !options.forceRefresh) {
      setData(cached);
      setIsLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setIsError(false);
      setError(null);
      
      const result = await apiRequest(endpoint, options);
      
      setData(result);
      setCachedData(cacheKey, result);
    } catch (err) {
      console.error(`❌ API request failed for ${endpoint}:`, err.message);
      setIsError(true);
      setError(err.message);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [endpoint]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = React.useCallback(() => {
    return fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

